import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const NalJodaniArjForm = () => {
  const componentRef = useRef();
  const { language } = useLanguage();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: 'Nal_Jodani_Arj.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'css', before: '.page-break' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .page-break {
              page-break-before: always;
            }
          }
        `}
      </style>
      
      {/* Controls Container - Hidden when printing */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'प्रिंट करा' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'पीडीएफ डाउनलोड करा' : 'Download PDF'}
        </button>
      </div>

      <div className="w-full overflow-x-auto flex justify-center">
        <div ref={componentRef} className="bg-white text-black shadow-lg print:shadow-none relative" style={{ width: '210mm' }}>
          
          {/* === PAGE 1 === */}
          <div className="px-10 py-8 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="text-center mb-1">
            <p className="text-sm">{language === 'marathi' ? 'महाराष्ट्र ग्रामपंचायत कर व फी (शुल्क) नियम १९६० चे नियम ९८ नुसार' : 'As per Rule 98 of Maharashtra Gram Panchayat Tax and Fee (Charge) Rules 1960'}</p>
          </div>

          <div className="text-center mb-4">
            <h1 className="text-2xl font-bold border-b-2 border-black inline-block pb-1">
              {language === 'marathi' ? 'नवीन नळ संयोजन घेणे करिता अर्ज' : 'Application for getting new water connection'}
            </h1>
          </div>

          <div className="flex justify-between mb-3 text-[15px]">
            <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
            <p>{language === 'marathi' ? 'दिनांक: &nbsp; &nbsp; &nbsp; / &nbsp; &nbsp; &nbsp; /२०' : 'Date: &nbsp; &nbsp; &nbsp; / &nbsp; &nbsp; &nbsp; /20'}</p>
          </div>

          <div className="mb-3 text-[15px] leading-relaxed">
            <p>{language === 'marathi' ? 'सरपंच / ग्रामपंचायत अधिकारी,' : 'Sarpanch / Gram Panchayat Officer,'}</p>
            
            <div className="mt-2">
              <p className="flex items-end mb-2">
                <span className="shrink-0 mr-1">{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'}</span>
                <span className="border-b border-dashed border-black w-64"></span>
              </p>
              <p className="flex items-end mt-1">
                <span className="shrink-0 mr-1">{language === 'marathi' ? 'ता.' : 'Taluka:'}</span>
                <span className="border-b border-dashed border-black w-48"></span>
                <span className="shrink-0 mx-1">{language === 'marathi' ? 'जि.' : 'Dist.:'}</span>
                <span className="border-b border-dashed border-black w-48"></span>
              </p>
            </div>
          </div>

          <div className="text-center mb-3 text-[16px] leading-relaxed font-bold">
             <p>{language === 'marathi' ? 'विषय:- नवीन नळ संयोजन (नळ जोडणी) मिळणेबाबत.' : 'Subject:- Regarding getting new water connection.'}</p>
          </div>

          <div className="pl-8 mb-3 text-[15px]">
            <p className="flex items-end mb-2">
              <span className="shrink-0 mr-2">{language === 'marathi' ? 'अर्जदार :- श्री/सौ./श्रीमती' : 'Applicant :- Shri/Smt/Mrs'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
              <span className="shrink-0 ml-2 mr-1">{language === 'marathi' ? 'मो.' : 'Mob.'}</span>
              <span className="border-b border-dashed border-black w-32"></span>
            </p>
            <p className="flex items-end pl-14">
              <span className="shrink-0 mr-2">{language === 'marathi' ? 'रा.' : 'Residing at'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
              <span className="shrink-0 mx-2">{language === 'marathi' ? 'ता.' : 'Taluka:'}</span>
              <span className="border-b border-dashed border-black w-32"></span>
              <span className="shrink-0 mx-2">{language === 'marathi' ? 'जि.' : 'Dist.:'}</span>
              <span className="border-b border-dashed border-black w-32"></span>
            </p>
          </div>

          <div className="mb-3 text-[15px] leading-relaxed">
            <p className="font-bold">{language === 'marathi' ? 'मा. महोदय,' : 'Respected Sir/Madam,'}</p>
            <div className="pl-8 mt-2 flex flex-wrap leading-loose">
               <span className="mr-1 mt-1">{language === 'marathi' ? 'उपरोक्त विषयान्वये विनंती अर्ज सादर करतो/करते की, मला माझ्या मालकीच्या/भोगवट्याच्या ग्रा.पं. घर' : 'With reference to the above subject, I submit an application requesting that I want a water connection in my owned/occupied Gram Panchayat house'}</span>
            </div>
            <div className="flex flex-wrap items-end leading-loose">
               <span className="mr-1 mt-1">{language === 'marathi' ? 'क्रमांक' : 'Number'}</span>
               <span className="border-b border-dashed border-black w-24 inline-block mt-1"></span>
               <span className="mx-1 mt-1">{language === 'marathi' ? 'मालकाचे नाव' : 'Owner\'s Name'}</span>
               <span className="border-b border-dashed border-black flex-grow inline-block mt-1"></span>
               <span className="ml-1 mt-1">{language === 'marathi' ? 'या' : 'in this'}</span>
            </div>
            <div className="flex flex-wrap items-end leading-loose">
               <span className="mt-1">{language === 'marathi' ? 'जागेवर / इमारतीत / मिळकतीत खालीलप्रमाणे नळ संयोजनाची आवश्यकता आहे.' : 'place / building / property as follows.'}</span>
            </div>
          </div>

          <div className="mb-3 text-[15px] flex items-center space-x-6">
            <span className="font-bold">{language === 'marathi' ? 'वापराचा प्रकार:' : 'Type of use:'}</span>
            <label className="flex items-center space-x-2 cursor-pointer">
              <div className="w-5 h-5 border border-black bg-white"></div>
              <span>{language === 'marathi' ? 'घरगुती' : 'Domestic'}</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <div className="w-5 h-5 border border-black bg-white"></div>
              <span>{language === 'marathi' ? 'घरगुती वापराव्यातिरिक्त इतर वापर (बिगर घरगुती)' : 'Other than domestic use (Non-domestic)'}</span>
            </label>
          </div>

          <div className="mb-3 text-[15px] flex items-center space-x-6">
            <span className="font-bold">{language === 'marathi' ? 'नळजोडणीचा व्यास :' : 'Water connection diameter :'}</span>
            <label className="flex items-center space-x-2 cursor-pointer">
              <div className="w-5 h-5 border border-black bg-white"></div>
              <span>{language === 'marathi' ? '१२ मिलीमीटर (१/२ इंच)' : '12 mm (1/2 inch)'}</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <div className="w-5 h-5 border border-black bg-white"></div>
              <span>{language === 'marathi' ? '२० मिलीमीटर (३/४ इंच)' : '20 mm (3/4 inch)'}</span>
            </label>
          </div>

          <div className="mb-3 text-[15px] leading-tight pl-2 space-y-1.5">
            <div className="flex">
               <span className="mr-2">1)</span>
               <p>{language === 'marathi' ? 'नळ संयोजनासाठी ग्रामपंचायतीने ठरविलेली अनामत रक्कम व नळ संयोजन फी भरण्यास मी तयार आहे.' : 'I am ready to pay the deposit amount and water connection fee fixed by the Gram Panchayat.'}</p>
            </div>
            <div className="flex">
               <span className="mr-2">2)</span>
               <p>{language === 'marathi' ? 'ग्रामपंचायतीने ठरविलेल्या विशेष पाणीपट्टी (खास पाणीपट्टी) च्या दरानुसार पाणीपट्टीची रक्कम मी नियमितपणे भरेल.' : 'I will regularly pay the water tax amount according to the special water tax rate fixed by the Gram Panchayat.'}</p>
            </div>
            <div className="flex">
               <span className="mr-2">3)</span>
               <p>{language === 'marathi' ? 'ग्रामपंचायतीच्या अधिकृत व्यक्तीकडूनच नळजोडणी करून घेईल व त्यामध्ये भविष्यात अनधिकृतरीत्या कुठलाही बदल करणार नाही.' : 'I will get the water connection done only by the authorized person of the Gram Panchayat and will not make any unauthorized change in it in the future.'}</p>
            </div>
            <div className="flex">
               <span className="mr-2">4)</span>
               <p className="text-justify">{language === 'marathi' ? 'पाणीपट्टी बील मिळाल्यापासून एका महिन्याच्या आत पाणीपट्टीची रक्कम न भरणे, आवश्यक दुरुस्ती व इतर आकार न भरणे, नळास तोट्या न बसविणे, मीटरमध्ये बिघाड करणे, पाण्याचा अपव्यय करणे, तसेच नळास विद्युत मोटार लावणे किंवा तत्सम कृत्य करून पाणीपुरवठा वितरणामध्ये अडथळा आणणे या पैकी कुठल्याही कारणामुळे माझे नळ संयोजन तोडले जाईल व मी कायदेशीर कार्यवाहीस पात्र राहील, याची मला जाणीव आहे.' : 'I am aware that my water connection will be cut off and I will be liable for legal action for any reason such as not paying water tax within one month of receiving bill, not paying repair & other charges, not fixing tap to the pipe, tampering with meter, wasting water, installing electric motor to the pipe or doing any such act causing obstruction in water supply distribution.'}</p>
            </div>
            <div className="flex">
               <span className="mr-2">5)</span>
               <p className="text-justify">{language === 'marathi' ? 'महाराष्ट्र ग्रामपंचायत अधिनियम, महाराष्ट्र ग्रामपंचायत कर व फी (शुल्क) नियम १९६० चे नियम ९७ ते १०४, नळ संयोजन संबंधित शासन निर्णय व ग्रामपंचायतीचे निर्णय मला मान्य राहतील.' : 'I will accept Maharashtra Gram Panchayat Act, Rules 97 to 104 of Maharashtra Gram Panchayat Tax and Fee Rules 1960, Govt decisions related to water connection and decisions of Gram Panchayat.'}</p>
            </div>
            <div className="flex">
               <span className="mr-2">6)</span>
               <div className="border-b border-dashed border-black flex-grow mt-4"></div>
            </div>
          </div>

          <div className="mb-4 pl-12 text-[15px]">
            <p>{language === 'marathi' ? 'करिता मला नळ संयोजन देण्यात यावे ही, विनंती.' : 'Therefore, I request that a water connection be given to me.'}</p>
          </div>

          <div className="flex justify-end text-[15px] mt-auto">
            <div className="flex items-end">
               <span className="mr-1">{language === 'marathi' ? 'अर्जदाराची सही व नाव :' : 'Applicant\'s signature and name :'}</span>
               <span className="border-b border-dashed border-black w-64"></span>
            </div>
          </div>

          <div className="absolute bottom-6 left-10 text-blue-700 text-sm underline print:hidden">
            www.majhigrampanchayat.com
          </div>
        </div>

        <div className="page-break"></div>

        {/* === PAGE 2 === */}
        <div className="px-10 py-8 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
          
          <div className="text-center mb-2 mt-2 print:mt-0">
            <p className="text-sm">{language === 'marathi' ? 'महाराष्ट्र ग्रामपंचायत कर व फी (शुल्क) नियम १९६० चे नियम ९८ नुसार' : 'As per Rule 98 of Maharashtra Gram Panchayat Tax and Fee (Charge) Rules 1960'}</p>
          </div>

          <div className="text-center mb-4">
            <h1 className="text-xl font-bold border-b-2 border-black inline-block pb-1">
              {language === 'marathi' ? 'जागा मालक यांचे संमतीपत्र' : 'Consent Letter of Land Owner'} <span className="font-normal text-sm">{language === 'marathi' ? '(*लागू असल्यास)' : '(*If applicable)'}</span>
            </h1>
          </div>

          <div className="mb-4 text-[15px] leading-relaxed">
            <div className="flex flex-wrap items-end pl-12">
              <span className="mr-1">{language === 'marathi' ? 'मी श्री./सौ./श्रीमती' : 'I Shri./Smt./Mrs'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
              <span className="mr-1">{language === 'marathi' ? 'रा.' : 'Residing at'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
              <span className="mx-2">{language === 'marathi' ? 'ता.' : 'Taluka:'}</span>
              <span className="border-b border-dashed border-black w-40"></span>
              <span className="mx-2">{language === 'marathi' ? 'जि.' : 'Dist.:'}</span>
              <span className="border-b border-dashed border-black w-40"></span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
              <span className="mr-1">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
              <span className="mx-2">{language === 'marathi' ? 'मिळकत क्र.' : 'Property No.'}</span>
              <span className="border-b border-dashed border-black w-32"></span>
              <span className="ml-1">{language === 'marathi' ? 'ही मिळकत माझ्या मालकीची असून' : 'this property is owned by me and'}</span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
              <span className="mr-1">{language === 'marathi' ? 'त्या ठिकाणी नवीन नळ संयोजनासाठी माझा अधिकृत अभिकर्ता/ भोगवटादार श्री./सौ./ श्रीमती' : 'for a new water connection at that place, my authorized agent / occupier Shri./Smt./ Mrs'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
              <span className="border-b border-dashed border-black w-64 mr-1"></span>
              <span className="mr-1">{language === 'marathi' ? 'यांनी ग्रामपंचायतीकडे अर्ज सादर केलेला असून त्यांनी सादर' : 'has submitted an application to the Gram Panchayat and I have'}</span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
              <span className="mr-1">{language === 'marathi' ? 'केलेल्या अर्जाप्रमाणे नळ संयोजन (नळ जोडणी) देण्यास माझी संमती आहे.' : 'my consent to give water connection as per the application submitted by them.'}</span>
            </div>
            <div className="pl-12 mt-1">
              <span>{language === 'marathi' ? 'करिता संमतीपत्र लिहून देत आहे.' : 'Therefore, giving this consent letter.'}</span>
            </div>
          </div>

          <div className="flex justify-between items-end mb-4 text-[15px]">
            <div className="w-1/2">
               <p className="flex items-end mb-4">
                  <span className="mr-2">{language === 'marathi' ? 'दिनांक:' : 'Date:'}</span>
               </p>
            </div>
            <div className="w-1/2 flex flex-col items-end pr-4 space-y-4">
               <p className="flex items-end w-full max-w-[250px]">
                 <span className="w-12">{language === 'marathi' ? 'सही:' : 'Signature:'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
               <p className="flex items-end w-full max-w-[250px]">
                 <span className="w-12">{language === 'marathi' ? 'नाव:' : 'Name:'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
               <p className="flex items-end w-full max-w-[250px]">
                 <span className="w-28 shrink-0">{language === 'marathi' ? 'मोबाईल क्रमांक:' : 'Mobile Number:'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
            </div>
          </div>

          <div className="border-b-2 border-dashed border-black mb-4 w-full"></div>

          <div className="text-center mb-3">
             <h2 className="text-xl font-bold">{language === 'marathi' ? 'आदेश:-' : 'Order:-'}</h2>
          </div>

          <div className="mb-4 text-[15px] leading-relaxed">
            <div className="flex flex-wrap items-end pl-12">
               <span className="mr-1">{language === 'marathi' ? 'वरिल प्रमाणे अर्जदार' : 'As above applicant'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
               <span className="mr-1">{language === 'marathi' ? 'रा.' : 'Residing at'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
               <span className="mx-2">{language === 'marathi' ? 'ता.' : 'Taluka:'}</span>
               <span className="border-b border-dashed border-black w-40"></span>
               <span className="mx-2">{language === 'marathi' ? 'जि.' : 'Dist.:'}</span>
               <span className="border-b border-dashed border-black w-40"></span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
               <span className="mr-1">{language === 'marathi' ? 'यांना ग्रामपंचायत घर क्र./मिळकत क्र.' : 'to them Gram Panchayat House No./Property No.'}</span>
               <span className="border-b border-dashed border-black w-32"></span>
               <span className="mx-1">{language === 'marathi' ? 'येथे १२ मिलीमीटर (१/२ इंच)/२०मिलिमीटर (३/४ इंच)' : 'here 12 mm (1/2 inch) / 20 mm (3/4 inch)'}</span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
               <span className="mr-1">{language === 'marathi' ? 'व्यासाचे नवीन नळ संयोजन (नळ जोडणी) देण्यात यावे. त्यांच्याकडून अनामत रक्कम रु.' : 'diameter new water connection should be given. Deposit amount Rs.'}</span>
               <span className="border-b border-dashed border-black w-32"></span>
               <span className="ml-1">{language === 'marathi' ? 'व' : 'and'}</span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
               <span className="mr-1">{language === 'marathi' ? 'नळ संयोजन फीस रु.' : 'water connection fee Rs.'}</span>
               <span className="border-b border-dashed border-black w-32"></span>
               <span className="ml-1">{language === 'marathi' ? 'भरणा केल्यानंतर जोडणी देण्यात यावी.' : 'connection should be given after payment.'}</span>
            </div>
          </div>

          <div className="flex justify-between items-end mb-4 text-[15px] pr-8">
             <p>{language === 'marathi' ? 'दिनांक:' : 'Date:'}</p>
             <p className="font-bold">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
          </div>

          <div className="border-b-2 border-dashed border-black mb-4 w-full"></div>

          <div className="text-center mb-3">
             <h2 className="text-xl font-bold">{language === 'marathi' ? 'आदेश:' : 'Order:'}</h2>
          </div>

          <div className="mb-4 text-[15px] leading-relaxed">
            <div className="flex flex-wrap items-end pl-12">
               <span className="mr-1">{language === 'marathi' ? 'वरिल प्रमाणे अर्जदार' : 'As above applicant'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
               <span className="mr-1">{language === 'marathi' ? 'रा.' : 'Residing at'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
               <span className="mx-2">{language === 'marathi' ? 'ता.' : 'Taluka:'}</span>
               <span className="border-b border-dashed border-black w-40"></span>
               <span className="mx-2">{language === 'marathi' ? 'जि.' : 'Dist.:'}</span>
               <span className="border-b border-dashed border-black w-40"></span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
               <span className="mr-1">{language === 'marathi' ? 'यांना ग्रामपंचायत घर क्र./मिळकत क्र.' : 'to them Gram Panchayat House No./Property No.'}</span>
               <span className="border-b border-dashed border-black w-32"></span>
               <span className="mx-1">{language === 'marathi' ? 'येथे १२ मिलीमीटर ( १/२ इंच) /२०मिलिमीटर (३/४ इंच)' : 'here 12 mm (1/2 inch) / 20 mm (3/4 inch)'}</span>
            </div>
            <div className="flex flex-wrap items-end mt-1">
               <span className="mr-1">{language === 'marathi' ? 'व्यासाचे नवीन नळ संयोजन (नळ जोडणी) खालील कारणांमुळे देण्यात येऊ नये. नाकारण्यात येत आहे.' : 'diameter new water connection should not be given due to the following reasons. Is being rejected.'}</span>
            </div>
            <div className="flex flex-wrap items-end mt-3">
               <span className="mr-2">{language === 'marathi' ? 'नाकारण्याचे कारण' : 'Reason for rejection'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
            </div>
          </div>

          <div className="flex justify-between items-end mt-auto pr-8 text-[15px]">
             <p>{language === 'marathi' ? 'दिनांक:' : 'Date:'}</p>
             <p className="font-bold">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
          </div>

        </div>

        </div>
      </div>
    </div>
  );
};

export default NalJodaniArjForm;
